<template>
  <div>
    <!-- Data Table Section -->
    <v-data-table-server
      :headers="headers"
      :items="listData.users"
      :items-length="pagination.totalItems"
      :loading="listData.loading"
      :items-per-page="pagination.itemsPerPage"
      :items-per-page-options="pagination.perPageOptions"
      v-model:page="pagination.currentPage"
      v-model:items-per-page="pagination.itemsPerPage"
      @update:options="$emit('load-items', $event)"
      class="elevation-0 data-table-modern"
    >

          <template v-slot:loading>
            <v-skeleton-loader class="mx-auto" max-width="100%" type="table-row@3"></v-skeleton-loader>
          </template>
          <template v-slot:loader>
          </template>

          <template v-slot:no-data>
            <div v-if="listData.error" class="text-center py-8">
              <v-icon size="64" color="danger-color" class="mb-4">mdi-alert-circle</v-icon>
              <h3 class="text-h6 text-danger-color mb-2">Error Loading Data</h3>
              <p class="text-body-2 text-grey-darken-1 mb-4">
                {{ listData.message || 'Failed to load archived users. Please try again.' }}
              </p>
              <v-btn color="danger-color" variant="outlined" @click="$emit('load-items', {})">
                <v-icon left>mdi-refresh</v-icon>
                Retry
              </v-btn>
            </div>
            <div v-else class="text-center py-8">
              <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-account-off</v-icon>
              <h3 class="text-h6 text-grey-darken-1 mb-2">No Accounts for Deletion</h3>
              <p class="text-body-2 text-grey-darken-1 mb-0">
                There are currently no user accounts scheduled for deletion.
              </p>
            </div>
          </template>

          <template v-slot:[`item.serial_no`]="{ item }">
            {{ ((pagination.currentPage - 1) * pagination.itemsPerPage) + listData.users.indexOf(item) + 1 }}
          </template>

          <template v-slot:[`item.name`]="{ item }">
            <v-badge :color="item.active ? 'success' : 'error'" dot class="cursor-pointer">
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <div v-bind="props">
                    <v-icon icon="mdi-account" size="default" :color="item.active ? 'success' : 'error'"></v-icon>
                  </div>
                </template>
                <span>{{ item.active ? 'Active User' : 'Suspended User' }}</span>
              </v-tooltip>
            </v-badge>
            &nbsp;
            <span class="text-brand-color opacity-70 cursor-not-allowed">{{ item.name }}</span>
          </template>

          <template v-slot:[`item.email`]="{ item }">
            <v-badge :color="item.verified ? 'success' : 'warning'" dot class="cursor-pointer">
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <div v-bind="props">
                    <v-icon icon="mdi-email" size="default" :color="item.verified ? 'success' : 'warning'"></v-icon>
                  </div>
                </template>
                <span>{{ item.verified ? 'Verified' : 'Unverified' }}</span>
              </v-tooltip>
            </v-badge>
            &nbsp;
            {{ item.email }}
          </template>

          <template v-slot:[`item.role`]="{ item }">
            <span class="text-left text-uppercase font-weight-medium">
              {{ userRolesMethod(item.roles) }}
            </span>
          </template>

          <template v-slot:[`item.forms_count`]="{ item }">
            <v-chip color="brand-color" variant="tonal">{{ item.forms_count || 0 }}</v-chip>
            <!-- <span :class="item.forms_count ? 'text-brand-color font-weight-bold' : 'text-grey'">
              {{ item.forms_count || 0 }}
            </span> -->
          </template>

          <template v-slot:[`item.user_leads_count`]="{ item }">
            <v-chip color="info" variant="tonal">{{ item.user_leads_count || 0 }}</v-chip>
            <!-- <span :class="item.user_leads_count ? 'text-brand-color font-weight-bold' : 'text-grey'">
              {{ item.user_leads_count || 0 }}
            </span> -->
          </template>

          <template v-slot:[`item.delete_at`]="{ item }">
            {{ localeDate(item.inactive_details?.delete_at || item.delete_at) }}
          </template>

          <template v-slot:[`item.delete_source`]="{ item }">
            <v-chip :color="getDeletionSourceColor(item.inactive_details?.delete_source)" size="small" label
              class="my-2">
              {{ getDeletionSourceText(item.inactive_details?.delete_source) }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-speed-dial v-if="item.inactive_details?.delete_source === 'manual_command'" v-model="item.speedDial"
              location="left center">
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" color="grey-lighten-3" icon size="x-small" class="elevation-0">
                  <v-icon>{{ item.speedDial ? 'mdi-close' : 'mdi-dots-vertical' }}</v-icon>
                </v-btn>
              </template>

              <a :key="`restore-${item.id}`" href="#" @click.prevent="openRestoreModal(item)"
                class="text-decoration-none">
                <v-btn color="success" icon size="x-small" variant="flat" :loading="restore.loader">
                  <v-icon>mdi-restore</v-icon>
                  <v-tooltip activator="parent" location="top">
                    <span>Cancel Deletion</span>
                  </v-tooltip>
                </v-btn>
              </a>
            </v-speed-dial>

            <v-tooltip v-else location="top">
              <template v-slot:activator="{ props }">
                <div v-bind="props">
                  <v-btn color="danger-color" icon size="x-small" variant="flat" disabled>
                    <v-icon>mdi-cancel</v-icon>
                  </v-btn>
                </div>
              </template>
              <span>No action allowed</span>
            </v-tooltip>
          </template>
    </v-data-table-server>

    <!-- Restore Confirmation Dialog -->
    <v-dialog v-model="restore.isOpenModal" max-width="550" persistent>
      <v-card>
        <div class="card-section-header">
          <div class="d-flex align-center ga-3">
            <v-avatar color="success" variant="tonal" size="36">
              <v-icon color="success">mdi-restore</v-icon>
            </v-avatar>
            <h2 class="section-title">Cancel User Deletion</h2>
          </div>
        </div>
        <v-card-text class="pa-6">
          <v-alert type="info" variant="tonal" icon="mdi-information">
            <p v-if="restore.form" class="mb-0">
              You are about to cancel the deletion for user <strong>{{ restore.form.name }}</strong>
              ({{ restore.form.email }}). This user will no longer be scheduled for deletion.
            </p>
            <p v-else class="mb-0">
              Are you sure you want to cancel deletion for this user?
            </p>
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer />
          <v-btn
            color="grey-darken-1"
            variant="outlined"
            @click="restore.isOpenModal = false"
          >
            Close
          </v-btn>
          <v-btn
            color="success"
            variant="flat"
            :loading="restore.loader"
            @click="confirmRestore"
          >
            Cancel Deletion
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar?.error ? 'error' : 'success'" :timeout="3000" rounded="lg" location="top">
      <p class="text-center font-weight-bold mb-0 text-white">{{ snackbar?.text }}</p>
    </v-snackbar>
  </div>
</template>

<script>
import helperMixin from '@/mixins/helper'
import userMixin from '@/mixins/user'
import { mapGetters } from 'vuex'

export default {
  mixins: [helperMixin, userMixin],
  props: {
    pagination: {
      type: Object,
      required: true
    },
    headers: {
      type: Array,
      required: true
    },
    listData: {
      type: Object,
      required: true
    },
    userRolesMethod: {
      type: Function,
      required: true
    },
    subscriptionMethods: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      restore: {
        isOpenModal: false,
        loader: false,
        message: null,
        form: null
      },
      snackbar: {
        text: '',
        error: false,
        show: false,
        timeout: 5000
      }
    }
  },
  computed: {
    ...mapGetters(['forms', 'token', 'user']),

    hasBasePermissions() {
      if (this.isAdmin || this.isSuperCustomer) {
        return true
      }
      const restrictedStates = [
        (this.userMixin_hasActiveFreeTrialPlan && (!this.userMixin_inTrial || this.userMixin_planExpired)),
        this.userMixin_hasActiveFreePlan,
        this.userMixin_isPastDueUser,
        (this.userMixin_hasPausedSubscription && !this.userMixin_hasPausedSubscriptionInGrace)
      ]
      return !restrictedStates.some(state => state === true)
    }
  },
  methods: {
    getDeletionSourceText(source) {
      const sourceMap = {
        'command': 'GDPR Automatic Deletion',
        'manual_command': 'User/Admin Initiated',
        'admin_force': 'MySQL Force Deletion'
      }
      return sourceMap[source] || source || 'Unknown'
    },
    getDeletionSourceColor(source) {
      const colorMap = {
        'command': 'danger',
        'manual_command': 'accent-color',
        'admin_force': 'danger'
      }
      return colorMap[source] || 'grey-darken-3'
    },

    showSnackbar({ text, error = false, timeout = 5000 }) {
      this.snackbar = {
        text,
        error,
        show: true,
        timeout
      }
    },
    openRestoreModal(form = null) {
      this.restore.form = form
      this.restore.isOpenModal = true
      this.restore.message = null
    },
    async confirmRestore() {
      this.restore.loader = true
      try {
        await this.$store.dispatch('user/cancelUserDelete', { userId: this.restore.form.id })
        this.restore.loader = false
        this.restore.isOpenModal = false
        this.showSnackbar({ text: 'User deletion cancelled successfully.', error: false })
        this.$emit('load-items', {})
      } catch (error) {
        this.restore.loader = false
        this.showSnackbar({ text: error?.response?.data?.message || 'Failed to cancel deletion.', error: true })
      }
    }
  },
  watch: {
    'restore.isOpenModal'(newVal) {
      if (!newVal) {
        this.restore.form = null
        this.restore.message = null
      }
    }
  }
}
</script>

